import Tippy from '@tippyjs/react';
import { UserAvatar } from 'components/user-avatar';
import { format } from 'date-fns-tz';
import { ptBR } from 'date-fns/locale';
import React, { ComponentType } from 'react';
import { BiReset } from 'react-icons/bi';
import { FiEdit3, FiEye } from 'react-icons/fi';
import { MdOutlineLock } from 'react-icons/md';
import { HiOutlineHandThumbUp, HiOutlineHandThumbDown } from 'react-icons/hi2';
import { GetManagerStepLabel, GetManagerTypeLabel, GetTaskLevelLabel } from 'utils/strings';
import ModalActions from './modal-actions';
import { RiMessage2Line } from 'react-icons/ri';
import { BsTrash } from 'react-icons/bs';
import ModalConfirm from './modal-actions/confirm';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import { RiProhibitedLine } from 'react-icons/ri';
import Matriz from 'components/matriz';
import Humanograma from 'components/Humanograma';
import { LuArrowDownWideNarrow } from 'react-icons/lu';
import { FaEye } from 'react-icons/fa';
import { managersByStepProps, TaskItemProps, useTaskItemEcoa } from './useTaskItemEcoa';

// import { updateTask } from 'services/dashboard.service';

const getStatusApprovalParticipants: any = {
  autoApproved: 'Número de participantes liberados automaticamente',
  pending: 'Número de participantes pendentes de autorização',
  approved: 'Número de participantes aprovados',
  rejected: 'Número de participantes não aprovados',
};

/**
 * Botões de ação do cabeçalho do card (excluir, editar, desfazer apreciação), no mesmo padrão do card da
 * Realização Destaque: compactos e numa linha só cada um; juntos, descem para baixo do nome quando não cabem
 * ao lado dele. No celular ocupam a largura do card, um por linha.
 */
const HEADER_ACTION_CLASS =
  'inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border px-3 py-2 text-xs font-semibold uppercase leading-none tracking-wide transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow sm:w-auto';

/**
 * Props do cartão ECOA. `Visual` troca só o desenho: recebe as mesmas props, inclusive as próprias dele, e o hook
 * `useTaskItemEcoa` continua sendo a única fonte das regras. Sem `Visual`, sai o cartão antigo (telas `/v3`).
 * A fila nova passa por aqui de propósito: é a fronteira que a caracterização das gravações da `ApreciarAcao` substitui.
 */
export type TaskItemEcoaProps<P extends TaskItemProps = TaskItemProps> = P & { Visual?: ComponentType<P> };

export function TaskItemEcoa<P extends TaskItemProps = TaskItemProps>({ Visual, ...props }: TaskItemEcoaProps<P>) {
  if (Visual) return <Visual {...(props as unknown as P)} />;
  return <CartaoAntigoEcoa {...props} />;
}

function CartaoAntigoEcoa(props: TaskItemProps) {
  const {
    activeEvaluationStep,
    activeStage,
    bonus,
    callBackActions,
    canApproveParticipants,
    canDecreaseParticipation,
    canExcludeParticipation,
    canFinalize,
    canSendFeedback,
    canValidate,
    canViewBoxActions,
    capitalizeWords,
    cycle,
    data,
    double,
    doubleByPassCod5,
    ecoa,
    evaluationData,
    getFormattedDate,
    getStepLabel,
    getUserTeam,
    handleDelete,
    handleEdit,
    hasFeedback,
    hasHeaderActions,
    isParticipantManager,
    isView,
    managerType,
    managersByStep,
    onDouble,
    oppened,
    pitStopCanEvaluate,
    playerInfo,
    points,
    promote,
    questionTitle,
    setOppened,
    setShowFeedbacks,
    setUserToChange,
    showDeleteButton,
    showEditButton,
    showFeedbacks,
    showModal,
    showResetButton,
    status,
    timeline,
    userToChange,
    validateTeamMember,
  } = useTaskItemEcoa(props);

  return (
    <>
      <div className="task-item bg-maastricht-blue-3 rounded-[20px] text-white py-6 px-3 lg:p-8" key={data._id + '_task'}>
        <div className="relative flex flex-col lg:flex-row items-center justify-between space-y-2 lg:space-y-0 lg:space-x-3 pb-5 mb-5 border-b-2 border-prussian-blue">
          <div className="flex w-full flex-wrap items-center gap-x-6 gap-y-4">
            <div className="flex min-w-0 flex-1 basis-[22rem] items-center space-x-3">
              <div>
                <UserAvatar data={data.owner_avatar} />
              </div>

              <Humanograma
                data={{
                  ...data,
                  nome: data.owner_name,
                  funcao: data.owner_funcao,
                  _id: data.owner,
                  prefixo: data.prefixo,
                  nome_dependencia: data.owner_nome_dependencia,
                }}
              >
                <div>
                  <p className="text-white text-base font-thin leading-none flex items-center space-x-1" >
                    <strong className="font-semibold">{data.owner_name.split(' ')[0]}</strong> <span>({data.owner})</span>{' '}
                    <span className="hidden lg:block alice-blue">cadastrou</span>
                  </p>

                  <span className="font-thin lg:hidden">cadastrou:</span>
                  <p className="text-police-blue text-xs font-light leading-none mt-2" >
                    {`Data do cadastro: ${format(new Date(data.created), 'd MMM. yy', {
                      locale: ptBR,
                    })}`}
                    &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                    {!!data.updated
                      ? `Última atualização: ${format(new Date(data.updated), 'd MMM. yy', {
                          locale: ptBR,
                        })}`
                      : ''}
                  </p>
                </div>
              </Humanograma>
            </div>

            {hasHeaderActions && (
              <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto sm:justify-end">
                {showDeleteButton && (
                  <button
                    onClick={() => showModal('confirm-delete', { id: data._id })}
                    type="button"
                    aria-label="Abrir diálogo para excluir ação"
                    aria-haspopup="dialog"
                    className={`${HEADER_ACTION_CLASS} border-[#ED2B48]/50 text-[#ED2B48] hover:bg-[#ED2B48]/10`}
                  >
                    <BsTrash size={16} aria-hidden="true" /> EXCLUIR AÇÃO
                  </button>
                )}

                {showEditButton && (
                  <button
                    onClick={handleEdit}
                    type="button"
                    aria-label="Abrir página para editar ação"
                    className={`${HEADER_ACTION_CLASS} border-azureish-white/30 text-azureish-white hover:bg-azureish-white/10`}
                  >
                    <FiEdit3 size={16} aria-hidden="true" /> EDITAR AÇÃO
                  </button>
                )}

                {showResetButton && (
                  <button
                    onClick={() => showModal(`validation-reset-${data._id}`)}
                    type="button"
                    aria-label="Abrir diálogo para desfazer apreciação"
                    aria-haspopup="dialog"
                    className={`${HEADER_ACTION_CLASS} border-azureish-white/30 text-azureish-white hover:bg-azureish-white/10`}
                  >
                    <BiReset size={16} aria-hidden="true" /> DESFAZER APRECIAÇÃO
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4 lg:space-y-7">
          {Array.isArray(managersByStep) && !!managersByStep.length && (
            <div>
              <h3 className="block text-base font-normal text-electric-blue mb-4" tabIndex={0}>Acompanhe o andamento da ação</h3>
              <div className="flex justify-between space-x-1 mb-4">
                {managersByStep.map((manager: managersByStepProps, idx: number) => (
                  <div key={idx + '_timeline'} className="flex-1 w-full flex flex-col">
                    <p
                      className="flex items-center space-x-1 text-xs leading-none font-light mb-4 h-[18px]"
                      style={{
                        color: timeline(manager)?.titleColor || '',
                      }}
                      tabIndex={0}
                    >
                      {!timeline(manager)?.opened ? <MdOutlineLock size={18} aria-hidden="true" /> : null}
                      <span>{manager.stageTitle}</span>
                    </p>

                    <div className="flex justify-between items-stretch h-[10px] rounded-lg overflow-hidden mb-3" aria-hidden="true">
                      <span
                        className="flex-1 w-full block h-full"
                        style={{
                          backgroundColor:
                            manager.stageNumber === 0 ? timeline(manager)?.bgColor : timeline(manager)?.bgColor1 || '',
                        }}
                      ></span>
                      {manager.stageNumber > 1 ? (
                        <>
                          <span className="w-[1px] block h-full"></span>
                          <span
                            className="flex-1 w-full block h-full"
                            style={{
                              backgroundColor: timeline(manager)?.bgColor2 || '',
                            }}
                          ></span>
                        </>
                      ) : null}
                    </div>

                    <p
                      className={`block text-sm leading-none ${
                        activeStage === manager.stageNumber ? 'font-normal' : 'font-light'
                      }`}
                      style={{
                        color: timeline(manager)?.statusColor || '',
                      }}
                      tabIndex={0}
                    >
                      {timeline(manager)?.status || ''}
                    </p>

                    {Array.isArray(timeline(manager)?.gestor) ? (
                      <div className="flex items-center space-x-2 mt-3">
                        {timeline(manager)?.gestor.map((gestor: any, index: number) => (
                          <React.Fragment key={index}>
                            <Humanograma data={{ ...gestor, _id: gestor?.chave }}>
                              <p className="text-xs leading-none font-light" tabIndex={0}>{capitalizeWords(gestor?.nome_guerra)}</p>
                            </Humanograma>
                            {timeline(manager)?.gestor?.length > 1 && index < timeline(manager)?.gestor.length - 1 && (
                              <span className="mx-1">/</span>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    ) : (
                      timeline(manager)?.gestor && (
                        <Humanograma data={{ ...timeline(manager)?.gestor, _id: timeline(manager)?.gestor?.chave }}>
                          <p className="text-xs leading-none font-light mt-3" tabIndex={0}>
                            {capitalizeWords(timeline(manager)?.gestor?.nome_guerra)}
                          </p>
                        </Humanograma>
                      )
                    )}
                  </div>
                ))}
              </div>

              <p className="block text-sm font-semibold" tabIndex={0}>
                Pontos recebidos: <strong className="text-base text-yellow font-semibold">{points}</strong>
              </p>
            </div>
          )}

          {/* {!!managerType && !!playerInfo && (
            <div className="space-y-3 lg:space-y-0 flex flex-col lg:flex-row justify-between">
              <div>
                <p className="text-sm font-thin text-electric-blue">Fluxo de Apreciação:</p>
                <p className="text-base font-normal text-white m-0">{stepLabel}</p>
              </div>
              <div>
                <p className="text-sm font-thin text-electric-blue">Prefixo desta entrega:</p>
                <p className="text-base font-normal text-white m-0">{data.prefixo}</p>
              </div>
            </div>
          )} */}

          <div>
            <p className="text-sm font-thin text-electric-blue" tabIndex={0}>Nome da ação</p>
            <p className="text-base font-normal text-white m-0" tabIndex={0}>{data.title || '---'}</p>
          </div>

          {!!oppened ? (
            <>
              <div>
                <p className="text-sm font-thin text-electric-blue" tabIndex={0}>Detalhes da ação</p>
                <p
                  className="text-base font-normal text-white m-0"
                  tabIndex={0}
                  dangerouslySetInnerHTML={{
                    __html:
                      typeof data.description === 'string'
                        ? data.description.replace(/\n/gi, '<br>').replace(/\t/gi, '&nbsp;&nbsp;&nbsp;&nbsp;')
                        : '',
                  }}
                />
              </div>
              <div>
                <p className="text-sm font-thin text-electric-blue" tabIndex={0}>Resultado da ação</p>
                <p
                  className="text-base font-normal text-white m-0"
                  tabIndex={0}
                  dangerouslySetInnerHTML={{
                    __html:
                      typeof data.result === 'string'
                        ? data.result.replace(/\n/gi, '<br>').replace(/\t/gi, '&nbsp;&nbsp;&nbsp;&nbsp;')
                        : '',
                  }}
                />
              </div>

              {!!data.indicator?.nome || !!data.participants_approval?.status ? (
                <div className="space-y-3 lg:space-y-0 flex flex-col lg:flex-row justify-between">
                  {!!data.indicator?.nome && (
                    <div className={!!data.participants_approval?.status ? `lg:w-1/2` : 'lg:w-full'}>
                      <p className="text-sm font-thin text-electric-blue" tabIndex={0}>Indicador</p>
                      <p className="text-base font-normal text-white m-0" tabIndex={0}>{data.indicator?.nome || '---'}</p>
                    </div>
                  )}

                  {!!data.participants_approval?.status && (
                    <div className={!!data.indicator?.nome ? `lg:w-1/3` : 'lg:w-full'}>
                      <p className="text-sm font-thin text-electric-blue" tabIndex={0}>Status de participação</p>
                      <p className="text-base font-normal text-white m-0" tabIndex={0}>
                        {cycle?.status !== 'open' && data.participants_approval.status === 'pending'
                          ? getStatusApprovalParticipants.rejected
                          : getStatusApprovalParticipants[data.participants_approval.status]}
                      </p>
                    </div>
                  )}
                </div>
              ) : null}

              {!!data?.nec?.cd_alcc && !!data?.nec?.nm_uor_sltt ? (
                <div className="space-y-3 lg:space-y-0 flex flex-col lg:flex-row justify-between">
                  <div className="lg:w-1/2">
                    <p className="text-sm font-thin text-electric-blue" tabIndex={0}>NEC</p>
                    <p className="text-base font-normal text-white m-0" tabIndex={0}>{data?.nec?.cd_alcc + ' - ' + data?.nec?.nm_alcc}</p>
                  </div>

                  <div className="lg:w-1/3">
                    <p className="text-sm font-thin text-electric-blue" tabIndex={0}>Demandante</p>
                    <p className="text-base font-normal text-white m-0" tabIndex={0}>{data?.nec?.nm_uor_sltt}</p>
                  </div>
                </div>
              ) : null}

              <div className="space-y-3 lg:space-y-0 flex flex-col lg:flex-row justify-between">
                <div className="lg:w-1/3">
                  <p className="text-sm font-thin text-electric-blue" tabIndex={0}>Período da entrega</p>
                  <p className="text-base font-normal text-white m-0" tabIndex={0}>
                    {getFormattedDate(data.start)} a {getFormattedDate(data.end)}
                  </p>
                </div>
                {!!data.nec?.length && (
                  <div className="lg:w-1/3">
                    <p className="text-sm font-thin text-electric-blue" tabIndex={0}>NEC - Necessidade de TI</p>
                    <p className="text-base font-normal text-white m-0" tabIndex={0}>{data.nec}</p>
                  </div>
                )}
              </div>

              {/* <div className="space-y-3 lg:space-y-0 flex flex-col-reverse lg:flex-row justify-between">
                <div className="lg:w-1/3">
                  <p className="text-sm font-thin text-electric-blue">Status da entrega</p>
                  <p className="text-base font-normal text-white m-0">
                    {GetTaskLevelLabel(isCurrentEvaluator() ? 'aguardando' : data.status)}
                  </p>
                </div>

                {data.evaluator &&
                data.evaluator.length > 0 &&
                !isNaN(Number(data.evaluator[data.evaluator.length - 1])) &&
                !!getEvaluatorData(data) &&
                data.status !== 'concluido' &&
                data.status !== 'concluidoAuto' &&
                data.status !== 'apreciacaoExpirada' &&
                data.status !== 'selo' ? (
                  <div className="lg:w-1/3">
                    <p className="text-sm font-thin text-electric-blue">Sendo avaliado por</p>
                    <p className="text-base font-normal text-white m-0">
                      {getEvaluatorData(data).chave} - {getEvaluatorData(data).nome_guerra}
                    </p>
                  </div>
                ) : null}

                <div className="lg:w-1/3">
                  <p className="text-sm font-thin text-electric-blue">Pontos a receber</p>
                  <p className="text-base font-normal text-white m-0">{points}</p>
                </div>
              </div> */}

              {!!data.feedback?.length &&
                !isView &&
                data.feedback
                  .sort((a: any, b: any) => b.etapa - a.etapa)
                  .map((feedback: any, index: number) => (
                    <div key={feedback._id + '_feedback'}>
                      {index === 0 && (
                        <button
                          onClick={() => setShowFeedbacks(!showFeedbacks)}
                          type="button"
                          aria-label={`${showFeedbacks ? 'Recolher' : 'Expandir'} feedbacks`}
                          aria-expanded={showFeedbacks}
                          className="text-electric-blue flex items-center space-x-2 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow rounded"
                        >
                          <p className="text-sm font-thin text-electric-blue">
                            Feedback {feedback.evaluatorId !== 'F0000000' ? 'do gestor' : ''}
                          </p>

                          {showFeedbacks ? <IoIosArrowUp size={20} aria-hidden="true" /> : <IoIosArrowDown size={20} aria-hidden="true" />}
                        </button>
                      )}

                      {showFeedbacks &&
                        (feedback.evaluatorId === 'F0000000' ? (
                          <>
                            <p className="text-base font-light text-white m-0 w-fit" tabIndex={0}>
                              {/* {getStepLabel(feedback.etapa).length ? `${getStepLabel(feedback.etapa)}: ` : ''} */}
                              {getStepLabel(feedback.etapa) === 'Validação' ? 'Avaliado' : 'Apreciado'} por{' '}
                              {feedback.evaluator_nome_guerra}
                            </p>
                            <p
                              className="text-base font-light text-white m-0 italic"
                              tabIndex={0}
                              dangerouslySetInnerHTML={{
                                __html: feedback.message,
                              }}
                            />
                          </>
                        ) : (
                          <>
                            <a
                              href={`https://humanograma.intranet.bb.com.br/${feedback.evaluatorId}`}
                              target="_blank"
                              rel="noreferrer"
                              className="hover:text-white focus:text-white w-fit focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow rounded"
                              aria-label={`Ver perfil de ${feedback.evaluator_nome_guerra} no Humanograma`}
                            >
                              <Tippy
                                allowHTML
                                content={
                                  <>
                                    <p className="m-0">
                                      <strong>{`${feedback.evaluator_nome} (${feedback.evaluatorId})`}</strong>
                                    </p>

                                    <p className="m-0 mt-2 text-electric-blue">Clique para o Humanograma</p>
                                  </>
                                }
                              >
                                <p className="text-base font-light text-white m-0 w-fit">
                                  {getStepLabel(feedback.etapa).length ? `${getStepLabel(feedback.etapa)}: ` : ''}
                                  {getStepLabel(feedback.etapa) === 'Validação' ? 'Avaliado' : 'Apreciado'} por{' '}
                                  {feedback.evaluator_nome_guerra}
                                </p>
                              </Tippy>{' '}
                            </a>
                            <p
                              className="text-base font-light text-white m-0 italic"
                              tabIndex={0}
                              dangerouslySetInnerHTML={{
                                __html: feedback.message,
                              }}
                            />
                          </>
                        ))}
                    </div>
                  ))}

              {!!data.target?.length && (
                <div className="space-y-3 lg:space-y-4 flex flex-col w-full">
                  <p className="text-base font-semibold leading-none m-0 text-yellow" tabIndex={0}>
                    Participantes da ação {`(${data.target?.length})`}
                  </p>

                  {data.target
                    .sort((a: any, b: any) => {
                      if (a?.name < b?.name) {
                        return -1;
                      }
                      if (a?.name > b?.name) {
                        return 1;
                      }
                      return 0;
                    })
                    .map((player: any) => (
                      <div
                        key={player._id + '_target'}
                        className={`flex flex-col bg-oxford-blue py-3 px-8 rounded-lg ${
                          canExcludeParticipation(player) || canDecreaseParticipation(player)
                            ? 'border border-electric-blue border-dotted border-opacity-40'
                            : ''
                        }`}
                      >
                        <div className="flex flex-col lg:flex-row lg:items-center space-y-2 lg:space-y-0 lg:space-x-4">
                          <div className="flex-1 flex flex-row items-center text-white space-x-3">
                            <UserAvatar data={{ ...player, ...player.extra }} />
                            <Humanograma data={{ ...player?.extra, _id: player._id, nome: player.name }}>
                              <span className="text-base font-thin text-white m-0" tabIndex={0}>
                                <span className="font-light">{player?.extra?.nome_guerra.split(' ')[0]}</span> ({player._id})
                                <p className="m-0 text-blue-light text-sm">
                                  {player?.extra?.funcao} - {player?.extra?.prefixo} - {player?.extra?.nome_dependencia}
                                </p>
                                {/* {!!showMessageRequiresApproval(player) ? (
                                <>
                                  <br />
                                  <span className="text-xs text-electric-blue leading-none block italic">
                                    Requer aprovação da participação pelo gestor
                                  </span>
                                </>
                              ) : null} */}
                              </span>
                            </Humanograma>
                          </div>

                          {!ecoa && (
                            <div className="bg-maastricht-blue px-4 py-2 text-xs font-thin leading-none text-yellow rounded-full flex items-center" tabIndex={0}>
                              {getUserTeam(player._id)?.percent || '0'}% de atuação
                            </div>
                          )}

                          {canDecreaseParticipation(player) && (
                            <div>
                              <Tippy
                                allowHTML
                                content={
                                  <>
                                    <p className="m-0 block text-center">
                                      {isParticipantManager(player)
                                        ? `Você pode diminuir esse percentual de atuação`
                                        : 'Você pode diminuir seu percentual de atuação'}
                                      .
                                    </p>
                                  </>
                                }
                              >
                                <button
                                  type="button"
                                  aria-label={`Abrir diálogo para diminuir percentual de atuação${isParticipantManager(player) ? ' do participante' : ''}`}
                                  aria-haspopup="dialog"
                                  className={`text-[#ED2B48] text-sm font-semibold block focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow rounded`}
                                  onClick={() => {
                                    setUserToChange(player._id);
                                    showModal(`team-member-decrease-${data._id}`);
                                  }}
                                >
                                  <LuArrowDownWideNarrow size={28} aria-hidden="true" />
                                </button>
                              </Tippy>
                            </div>
                          )}

                          {canExcludeParticipation(player) && (
                            <div>
                              <Tippy
                                allowHTML
                                content={
                                  <>
                                    <p className="m-0 block text-center">
                                      {isParticipantManager(player)
                                        ? `Você pode excluir essa participação`
                                        : 'Você pode excluir sua participação'}
                                      .
                                    </p>
                                  </>
                                }
                              >
                                <button
                                  type="button"
                                  aria-label={`Abrir diálogo para excluir participação${isParticipantManager(player) ? ' do participante' : ''}`}
                                  aria-haspopup="dialog"
                                  className={`text-[#ED2B48] text-sm font-semibold block focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow rounded`}
                                  onClick={() => {
                                    setUserToChange(player._id);
                                    showModal(`team-member-exclude-${data._id}`);
                                  }}
                                >
                                  <RiProhibitedLine size={28} aria-hidden="true" />
                                </button>
                              </Tippy>
                            </div>
                          )}
                        </div>
                        {/* {canViewBoxApproveTeamMember(player) ? (
                        <div className="w-full border-t-2 border-prussian-blue mt-4 pt-4 pb-2 lg:mt-6 lg:pt-6 lg:pb-3 block">
                          <p className="text-sm text-alice-blue block text-center mb-4 lg:mb-7 font-light">
                            Deseja aceitar a indicação do funcionário a esta ação?
                          </p>
                          <div className="flex items-center justify-center space-x-16">
                            <button
                              className="text-[#04D475] uppercase text-sm p-0 m-0 font-normal block"
                              onClick={() => [
                                showModal(`validation-team-member-accept-${data._id}`),
                                setValidateTeamMember(player._id),
                              ]}
                            >
                              ACEITAR
                            </button>
                            <button
                              className="text-[#ED2B48] uppercase text-sm p-0 m-0 font-normal block"
                              onClick={() => [
                                showModal(`validation-team-member-reject-${data._id}`),
                                setValidateTeamMember(player._id),
                              ]}
                            >
                              RECUSAR
                            </button>
                          </div>
                        </div>
                      ) : null} */}
                      </div>
                    ))}
                </div>
              )}
            </>
          ) : null}

          <button
            onClick={() => setOppened(prev => !prev)}
            type="button"
            aria-label={`${oppened ? 'Ver menos' : 'Ver mais'} informações da ação`}
            aria-expanded={oppened}
            className="mt-8 p-0 uppercase space-x-1 leading-none text-grey-dark text-sm font-normal flex items-center justify-center mx-auto focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow rounded"
          >
            {!oppened ? <IoIosArrowDown size={20} aria-hidden="true" /> : <IoIosArrowUp size={20} aria-hidden="true" />}{' '}
            <span>Ver {!!oppened ? 'menos' : 'mais'} informações</span>
          </button>
        </div>

        {(canViewBoxActions() || pitStopCanEvaluate) && !isView ? (
          <>
            <div className="w-full border-t-2 border-prussian-blue mt-4 pt-4 lg:mt-6 lg:pt-6 block">
              {pitStopCanEvaluate ? (
                <h3 className="text-electric-blue text-base leading-none mb-7 font-light block text-center" tabIndex={0}>
                  Essa Realização Destaque foi promovida pela sua Rede.
                </h3>
              ) : null}

              {!!questionTitle && (
                <h3 className="mb-8 p-0 leading-none block text-center font-light text-alice-blue text-base" tabIndex={0}>{questionTitle}</h3>
              )}

              <div className="flex flex-row space-x-16 justify-center items-center">
                {/* {!promote && !double && status === 'aprovado' ? (
                  <>
                    <button
                      className={`cursor-default text-opacity-60 text-[#04D475] space-x-2 inline-flex justify-center items-center uppercase leading-none text-sm`}
                    >
                      <HiOutlineHandThumbUp className="text-xl" />{' '}
                      <span className="uppercase leading-none text-sm font-normal">Validada</span>
                    </button>
                  </>
                ) : null}

                {status === 'rejeitado' ? (
                  <>
                    <button
                      className={`cursor-default text-opacity-60 text-[#ED2B48] space-x-2 inline-flex justify-center items-center uppercase leading-none text-sm`}
                    >
                      <HiOutlineHandThumbDown className="text-xl" />{' '}
                      <span className="uppercase leading-none text-sm font-normal">Devolvida</span>
                    </button>
                  </>
                ) : null} */}

                {!data.stageNumber && (!status || status === 'aguardando') && (
                  <>
                    {canValidate && (
                      <button
                        onClick={() => showModal(`validation-accept-${data._id}`)}
                        type="button"
                        aria-label="Abrir diálogo para validar ação"
                        aria-haspopup="dialog"
                        className={`text-[#04D475] space-x-2 inline-flex justify-center items-center uppercase leading-none text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow rounded`}
                      >
                        <HiOutlineHandThumbUp className="text-xl" aria-hidden="true" />{' '}
                        <span className="uppercase leading-none text-sm font-normal">Validar</span>
                      </button>
                    )}

                    <button
                      onClick={() => showModal(`validation-known-${data._id}`)}
                      type="button"
                      aria-label="Abrir diálogo para marcar como ciente"
                      aria-haspopup="dialog"
                      className={`text-[#04D475] space-x-2 inline-flex justify-center items-center uppercase leading-none text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow rounded`}
                    >
                      <FiEye className="text-xl" aria-hidden="true" />{' '}
                      <span className="uppercase leading-none text-sm font-normal">Dar como ciente</span>
                    </button>
                  </>
                )}

                {promote || double || pitStopCanEvaluate || canFinalize ? (
                  <>
                    {double || canFinalize ? (
                      // Matriz de apreciação - Não excluir
                      // !userQuizAnswered && playerStep !== 5 ? (
                      //   <>
                      //     <button
                      //       onClick={() => setShowModalMatriz(true)}
                      //       className={`text-[#04D475] space-x-2 inline-flex justify-center items-center uppercase leading-none text-sm`}
                      //     >
                      //       <HiOutlineHandThumbUp className="text-xl" />{' '}
                      //       <span className="uppercase leading-none text-sm font-normal">SIM</span>
                      //     </button>

                      //     <button
                      //       onClick={() => showModal(`validation-stop-quiz-${data._id}`)}
                      //       className={`text-[#ED2B48] space-x-2 inline-flex justify-center items-center uppercase leading-none text-sm`}
                      //     >
                      //       <HiOutlineHandThumbDown className="text-xl" />{' '}
                      //       <span className="uppercase leading-none text-sm font-normal">NÃO</span>
                      //     </button>
                      //   </>
                      // ) : (
                      <>
                        {double && (
                          <button
                            onClick={() =>
                              !!doubleByPassCod5 && typeof onDouble === 'function'
                                ? onDouble(data)
                                : showModal(`validation-double-${data._id}`)
                            }
                            type="button"
                            aria-label="Abrir diálogo para bonificar ação"
                            aria-haspopup="dialog"
                            className={`text-[#04D475] space-x-2 inline-flex justify-center items-center uppercase leading-none text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow rounded`}
                          >
                            <span className="uppercase leading-none text-sm font-normal">Bonificar</span>
                          </button>
                        )}

                        <button
                          onClick={() => showModal(`validation-stop-${data._id}`, { type: 'double' })}
                          type="button"
                          aria-label="Abrir diálogo para finalizar apreciação"
                          aria-haspopup="dialog"
                          className={`text-[#ED2B48] space-x-2 inline-flex justify-center items-center uppercase leading-none text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow rounded`}
                        >
                          <span className="uppercase leading-none text-sm font-normal">Finalizar</span>
                        </button>
                      </>
                    ) : // )
                    null}

                    {promote || pitStopCanEvaluate ? (
                      <>
                        <Tippy
                          arrow={false}
                          className="bg-yellow drop-shadow-lg text-white"
                          allowHTML={true}
                          content={
                            pitStopCanEvaluate
                              ? 'Você deve avaliar se essa entrega irá prosseguir para o último nível de apreciação (Diretor/Gerente Geral UE)'
                              : managerType && managerType === 'diretoria'
                                ? 'Podem receber Selos Realização Destaque até 10% das entregas bonificadas neste nível (com arredomento para cima)'
                                : 'Podem ser promovidas até 10% das entregas bonificadas neste nível (com arredomento para cima)'
                          }
                        >
                          <button
                            onClick={() =>
                              managerType && managerType === 'diretoria'
                                ? showModal(`validation-selo-${data._id}`)
                                : showModal(`validation-promote-${data._id}`)
                            }
                            type="button"
                            aria-label={`Abrir diálogo para ${managerType && managerType === 'diretoria' ? 'premiar com selo' : pitStopCanEvaluate ? 'permitir prosseguimento' : 'promover ação'}`}
                            aria-haspopup="dialog"
                            className={`text-[#04D475] space-x-2 inline-flex justify-center items-center uppercase leading-none text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow rounded`}
                          >
                            {/* <HiOutlineHandThumbUp className="text-xl" />{' '} */}
                            <span className="uppercase leading-none text-sm font-normal">
                              {managerType && managerType === 'diretoria'
                                ? 'Premiar com Selo Realização Destaque'
                                : pitStopCanEvaluate
                                  ? 'SIM'
                                  : 'Promover'}
                            </span>
                            {/* {managerType && managerType === 'diretoria' ? (
                                  <TfiMedallAlt className="text-xl" />
                                ) : (
                                  <BiUpvote className={`text-xl ${pitStopCanEvaluate ? 'rotate-90 transform' : ''}`} />
                                )}{' '}
                                <span className="uppercase leading-none text-sm font-normal">
                                  {managerType && managerType === 'diretoria'
                                    ? 'Indicar Selo'
                                    : pitStopCanEvaluate
                                    ? 'Prosseguir'
                                    : 'Promover'}
                                </span> */}
                          </button>
                        </Tippy>

                        {pitStopCanEvaluate ? (
                          <Tippy
                            arrow={false}
                            className="bg-yellow drop-shadow-lg text-white"
                            allowHTML={true}
                            content={
                              'Você deve avaliar se essa entrega será concluída neste nível e não será enviada para o último nível de apreciação (Diretor/Gerente Geral UE).'
                            }
                          >
                            <button
                              onClick={() => showModal(`validation-stop-${data._id}`, { type: 'pitstop' })}
                              type="button"
                              aria-label="Abrir diálogo para não permitir prosseguimento"
                              aria-haspopup="dialog"
                              className={`text-[#ED2B48] space-x-2 inline-flex justify-center items-center uppercase leading-none text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow rounded`}
                            >
                              <span className="uppercase leading-none text-sm font-normal">NÃO</span>
                            </button>
                          </Tippy>
                        ) : (
                          <button
                            onClick={() => showModal(`validation-stop-${data._id}`, { type: 'promote' })}
                            type="button"
                            aria-label="Abrir diálogo para finalizar apreciação"
                            aria-haspopup="dialog"
                            className={`text-[#ED2B48] space-x-2 inline-flex justify-center items-center uppercase leading-none text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow rounded`}
                          >
                            <span className="uppercase leading-none text-sm font-normal">Finalizar</span>
                          </button>
                        )}
                      </>
                    ) : null}
                  </>
                ) : null}
              </div>
            </div>
          </>
        ) : canViewBoxActions() && !!canApproveParticipants ? (
          <>
            <div className="w-full border-t-2 border-prussian-blue mt-4 pt-4 lg:mt-6 lg:pt-6 block">
              <h3 className="mb-8 p-0 leading-none block text-center font-light text-alice-blue text-base" tabIndex={0}>
                Esta ação possui {data?.team?.length} participantes. Você autoriza esse número de participantes?
              </h3>

              <div className="flex flex-row space-x-16 justify-center items-center">
                {data?.participants_approval?.status === 'approved' ? (
                  <>
                    <button
                      className={`cursor-default text-opacity-60 text-[#04D475] space-x-2 inline-flex justify-center items-center uppercase leading-none text-sm`}
                    >
                      <HiOutlineHandThumbUp className="text-xl" />{' '}
                      <span className="uppercase leading-none text-sm font-normal">SIM</span>
                    </button>
                  </>
                ) : null}

                {data?.participants_approval?.status === 'rejected' ? (
                  <>
                    <button
                      className={`cursor-default text-opacity-60 text-[#ED2B48] space-x-2 inline-flex justify-center items-center uppercase leading-none text-sm`}
                    >
                      <HiOutlineHandThumbDown className="text-xl" />{' '}
                      <span className="uppercase leading-none text-sm font-normal">NÃO</span>
                    </button>
                  </>
                ) : null}

                {data?.participants_approval?.status === 'pending' && cycle?.status === 'open' && (
                  <>
                    <button
                      onClick={() => showModal(`validation-participants-accept-${data._id}`)}
                      type="button"
                      aria-label="Abrir diálogo para autorizar número de participantes"
                      aria-haspopup="dialog"
                      className={`text-[#04D475] space-x-2 inline-flex justify-center items-center uppercase leading-none text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow rounded`}
                    >
                      <HiOutlineHandThumbUp className="text-xl" aria-hidden="true" />{' '}
                      <span className="uppercase leading-none text-sm font-normal">SIM</span>
                    </button>
                    <button
                      onClick={() => showModal(`validation-participants-reject-${data._id}`)}
                      type="button"
                      aria-label="Abrir diálogo para rejeitar número de participantes"
                      aria-haspopup="dialog"
                      className={`text-[#ED2B48] space-x-2 inline-flex justify-center items-center uppercase leading-none text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow rounded`}
                    >
                      <HiOutlineHandThumbDown className="text-xl" aria-hidden="true" />{' '}
                      <span className="uppercase leading-none text-sm font-normal">NÃO</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        ) : null}

        {canSendFeedback() && typeof hasFeedback === 'boolean' ? (
          <div className="mt-14">
            <h3 className="mb-8 p-0 leading-none block text-center font-light text-alice-blue text-base" tabIndex={0}>
              Conte à equipe o que achou dessa entrega:
            </h3>

            {!hasFeedback ? (
              <>
                <button
                  onClick={() => showModal(`validation-feedback-${data._id}`)}
                  type="button"
                  aria-label="Abrir diálogo para enviar feedback da ação"
                  aria-haspopup="dialog"
                  className={`text-[#DCE3F8] space-x-2 mx-auto flex justify-center items-center uppercase leading-none text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow rounded`}
                >
                  <RiMessage2Line className="text-xl" aria-hidden="true" />{' '}
                  <span className="uppercase leading-none text-sm font-normal">Enviar feedback</span>
                </button>
              </>
            ) : (
              <>
                <button
                  disabled
                  type="button"
                  aria-label="Feedback já enviado para esta ação"
                  className={`cursor-default text-opacity-60 text-[#DCE3F8] space-x-2 mx-auto flex justify-center items-center uppercase leading-none text-sm`}
                >
                  <RiMessage2Line className="text-xl" aria-hidden="true" />{' '}
                  <span className="uppercase leading-none text-sm font-normal">Feedback enviado</span>
                </button>
              </>
            )}
          </div>
        ) : null}

        {evaluationData}
      </div>

      <ModalActions
        callback={callBackActions}
        id={data._id}
        status={status}
        data={data}
        playerInfo={playerInfo}
        validateTeamMember={validateTeamMember}
        activeEvaluationStep={activeEvaluationStep as number}
        bonus={bonus}
        userToChange={userToChange}
        ecoa={ecoa}
      />

      <ModalConfirm callback={item => handleDelete(item.id)} />

      {/* // Matriz de apreciação - Não excluir */}
      {/* {showModalMatriz && (
        <Matriz
          onClose={() => setShowModalMatriz(false)}
          setQuizAnswered={() => setQuizAnswered(true)}
          taskId={data._id}
          playerInfo={playerInfo}
        />
      )} */}
    </>
  );
}
